import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { todayISO, isSchoolDay, getHolidayName, HOLIDAY_MESSAGE } from "@/lib/holiday";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Loader2, Save, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import HolidayNotice from "@/components/HolidayNotice";
import SantriPhoto from "@/components/SantriPhoto";

const STATUS_OPTIONS = [
  { v: "hadir", label: "Hadir", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { v: "izin", label: "Izin", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  { v: "sakit", label: "Sakit", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { v: "alfa", label: "Alfa", cls: "bg-red-100 text-red-800 border-red-200" },
];

export default function AbsensiSantriPage() {
  const { user } = useAuth();
  const [tanggal, setTanggal] = useState(todayISO());
  const [kelasList, setKelasList] = useState([]);
  const [kelasId, setKelasId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false);

  const holidayName = getHolidayName(tanggal);
  const isHoliday = !isSchoolDay(tanggal);

  const loadKelas = useCallback(async () => {
    try {
      const { data } = await api.get("/kelas", { params: { limit: 200 } });
      const items = data.items || [];
      setKelasList(items);
      if (!kelasId && items.length > 0) setKelasId(items[0].id);
    } catch (_e) { /* ignore */ }
  }, [kelasId]);

  useEffect(() => { loadKelas(); }, [loadKelas]);

  const load = useCallback(async () => {
    if (!kelasId || !tanggal) return;
    if (isHoliday) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/absensi/santri", { params: { tanggal, kelas_id: kelasId } });
      setRows(data.items || []);
      setAlreadySaved(data.already_saved);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tanggal, kelasId, isHoliday]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const setAll = (status) => {
    setRows((rs) => rs.map((r) => ({ ...r, status })));
  };

  const handleSave = async () => {
    const unset = rows.filter((r) => !r.status);
    if (unset.length > 0) {
      toast.error(`Masih ada ${unset.length} santri yang belum diisi statusnya.`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/absensi/santri/bulk", {
        tanggal, kelas_id: kelasId,
        entries: rows.map((r) => ({
          santri_id: r.santri_id, status: r.status, keterangan: r.keterangan || null,
        })),
      });
      toast.success(`Absensi tersimpan (${rows.length} santri).`);
      setAlreadySaved(true);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  // For ustadz: filter kelas dropdown only shows their assigned kelas (backend already filters /kelas)
  void user;

  return (
    <div className="space-y-6" data-testid="absensi-santri-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Absensi</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Absensi Santri</h1>
        <p className="text-slate-500 text-sm">Catat kehadiran santri per kelas dan tanggal.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-medium text-slate-600">Tanggal</Label>
            <Input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)}
                   data-testid="absensi-santri-tanggal" className="mt-1 h-10" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Kelas</Label>
            <Select value={kelasId} onValueChange={setKelasId}>
              <SelectTrigger data-testid="absensi-santri-kelas" className="mt-1 h-10">
                <SelectValue placeholder="Pilih kelas" />
              </SelectTrigger>
              <SelectContent>
                {kelasList.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-slate-500">Belum ada kelas.</div>
                ) : kelasList.map((k) => (
                  <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isHoliday && rows.length > 0 && (
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => setAll("hadir")}
                data-testid="absensi-santri-mark-all-hadir"
                className="h-10 w-full"
              >
                Tandai Semua Hadir
              </Button>
            </div>
          )}
        </div>
      </Card>

      {isHoliday ? (
        <HolidayNotice holidayName={holidayName} message={HOLIDAY_MESSAGE} />
      ) : (
        <>
          {alreadySaved && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                 data-testid="absensi-santri-already-saved">
              <CheckCircle2 className="h-4 w-4" />
              Absensi pada tanggal ini sudah tersimpan. Anda dapat mengedit dan menyimpan ulang.
            </div>
          )}

          <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
            {loading ? (
              <div className="py-16 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-slate-400">
                <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                Belum ada santri di kelas ini.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((r, idx) => (
                  <li key={r.santri_id} data-testid={`absensi-santri-row-${r.santri_id}`}
                      className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 hover:bg-emerald-50/30">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <SantriPhoto path={r.foto_path} alt={r.nama} />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{r.nama}</div>
                        <div className="text-xs text-slate-500 font-mono">{r.nis}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {STATUS_OPTIONS.map((opt) => {
                        const active = r.status === opt.v;
                        return (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => updateRow(idx, { status: opt.v })}
                            data-testid={`absensi-santri-status-${r.santri_id}-${opt.v}`}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${
                              active ? opt.cls + " shadow-sm scale-105" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      placeholder="Keterangan (opsional)"
                      value={r.keterangan || ""}
                      onChange={(e) => updateRow(idx, { keterangan: e.target.value })}
                      data-testid={`absensi-santri-ket-${r.santri_id}`}
                      className="w-full md:w-60 h-9 text-sm"
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {rows.length > 0 && (
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving}
                      data-testid="absensi-santri-save"
                      className="bg-emerald-600 hover:bg-emerald-700 h-11 px-6 gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Simpan Absensi
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
